import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import config from 'src/config';
import auth from './auth';
import { IAuthResponse } from 'src/lib/schemas';
import * as Sentry from '@sentry/browser';
import { safeLog, safeWarn, safeError, reportSentryError } from './logging';

const baseURL = config.app.apiURL.split('/v1')[0];

const axiosClient = axios.create({
	baseURL,
});

let refreshAttempts = 0;
let lastRefreshAttempt: number | null = null;
const MAX_REFRESH_ATTEMPTS = 3;
const BASE_DELAY = 2000;
let currentRefreshPromise: Promise<IAuthResponse | undefined> | null = null;
let guestRegisterAttempts = 0;
const MAX_GUEST_REGISTER_ATTEMPTS = 3;
let currentGuestPromise: Promise<void> | null = null;

const isDistilleryGooglePath = (): boolean => {
	return window.location.pathname.includes('/assistants/distillery-google');
};

const resetRefreshState = () => {
	refreshAttempts = 0;
	currentRefreshPromise = null;
	lastRefreshAttempt = null;
};

const resetGuestRegisterState = () => {
	guestRegisterAttempts = 0;
};

// Exponential backoff
const getNextRefreshDelay = (): number => {
	return BASE_DELAY * Math.pow(2, refreshAttempts - 1);
};

const canAttemptRefresh = (): boolean => {
	if (!lastRefreshAttempt) return true;

	const now = Date.now();
	const delay = getNextRefreshDelay();
	return now - lastRefreshAttempt >= delay;
};

const ensureGuestAuth = async (
	config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
	while (guestRegisterAttempts < MAX_GUEST_REGISTER_ATTEMPTS) {
		try {
			if (!currentGuestPromise) {
				currentGuestPromise = (async () => {
					safeLog('[Distillery] Attempting guest login...');
					Sentry.captureMessage('[Distillery] Attempting guest login...');
					await auth.registerGuest();
				})();
			}
			await currentGuestPromise;
			const newTokens = auth.getTokens();
			if (newTokens) {
				safeLog('[Distillery] Guest login successful.');
				Sentry.captureMessage('[Distillery] Guest login successful.');
				config.headers.set('Authorization', `Bearer ${newTokens.access.token}`);
				resetGuestRegisterState();
				return config;
			} else {
				safeWarn('[Distillery] Guest login did not return valid tokens.');
				Sentry.captureMessage(
					'[Distillery] Guest login did not return valid tokens.',
				);
			}
		} catch (error) {
			safeError(
				`[Distillery] Error during guest login (attempt ${guestRegisterAttempts + 1}):`,
				error,
			);
			reportSentryError(
				`[Distillery] Error during guest login (attempt ${guestRegisterAttempts + 1})`,
				error,
			);
		} finally {
			currentGuestPromise = null;
		}
		guestRegisterAttempts++;
		if (guestRegisterAttempts < MAX_GUEST_REGISTER_ATTEMPTS) {
			await new Promise((resolve) =>
				setTimeout(
					resolve,
					BASE_DELAY * Math.pow(2, guestRegisterAttempts - 1),
				),
			);
		}
	}

	safeError('[Distillery] Guest login failed after maximum attempts.');
	Sentry.captureMessage(
		'[Distillery] Guest login failed after maximum attempts.',
	);
	return config;
};

const refreshTokenIfNeeded = async (
	config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
	const tokens = auth.getTokens();
	if (!tokens) {
		return config;
	}

	const expiresAt = new Date(tokens.access.expires);
	const now = new Date();

	// If token is expired or about to expire, try to refresh it
	if (now.getTime() > expiresAt.getTime()) {
		try {
			if (refreshAttempts < MAX_REFRESH_ATTEMPTS && canAttemptRefresh()) {
				refreshAttempts++;
				lastRefreshAttempt = Date.now();

				// If a refresh is already in progress, wait for its result
				if (!currentRefreshPromise) {
					currentRefreshPromise = auth.refreshToken();
				}

				const refreshResponse = await currentRefreshPromise;

				if (refreshResponse?.tokens) {
					config.headers.set(
						'Authorization',
						`Bearer ${refreshResponse.tokens.access.token}`,
					);
					resetRefreshState();
				} else if (!isDistilleryGooglePath()) {
					auth.cleanupSession();
					window.location.href = '/login';
				}
			}
		} catch (error) {
			console.error('Error refreshing token:', error);
			resetRefreshState();

			if (!isDistilleryGooglePath()) {
				auth.cleanupSession();
				window.location.href = '/login';
			}
		}
	} else {
		config.headers.set('Authorization', `Bearer ${tokens.access.token}`);
	}

	return config;
};

const setDefaultInterceptors = (axiosInstance: AxiosInstance) => {
	axiosInstance.interceptors.request.use(
		async (config: InternalAxiosRequestConfig) => {
			const tokens = auth.getTokens();

			if (tokens) {
				config = await refreshTokenIfNeeded(config);
			}

			config.headers.set('Source', 'backoffice-fe');

			if (config.data && !(config.data instanceof FormData)) {
				config.headers.set('Content-Type', 'application/json');
			}

			return config;
		},
	);

	axiosInstance.interceptors.response.use(
		(response) => response,
		async (error) => {
			const { response, config } = error;

			if (response === undefined) {
				console.error('Error without response', error);
				return Promise.reject(error);
			}

			if (response.status >= 500) {
				console.error('Server error:', response);
			}

			if (response.status === 401) {
				if (isDistilleryGooglePath()) {
					config.headers = config.headers || {};
					const newConfig = await ensureGuestAuth(config);
					return axiosClient(newConfig);
				}

				if (config.url?.includes('verify-email')) {
					window.location.href = '/pending-verification';
					return Promise.reject(error);
				}

				if (!config.sent) {
					try {
						if (refreshAttempts < MAX_REFRESH_ATTEMPTS && canAttemptRefresh()) {
							config.sent = true;
							refreshAttempts++;
							lastRefreshAttempt = Date.now();

							if (!currentRefreshPromise) {
								currentRefreshPromise = auth.refreshToken();
							}

							const refreshResponse = await currentRefreshPromise;

							if (refreshResponse?.tokens) {
								config.headers.set(
									'Authorization',
									`Bearer ${refreshResponse.tokens.access.token}`,
								);
								resetRefreshState();
								return axiosClient(config);
							}
						}
					} catch (refreshError) {
						console.error('Error refreshing token:', refreshError);
						resetRefreshState();
						auth.cleanupSession();
						window.location.href = '/login';
					}
				}
			}

			return Promise.reject(error);
		},
	);
};

setDefaultInterceptors(axiosClient);

export default {
	get: axiosClient.get,
	post: axiosClient.post,
	put: axiosClient.put,
	patch: axiosClient.patch,
	delete: axiosClient.delete,
	setDefaultInterceptors: setDefaultInterceptors,
};
